'use client'
import * as React from 'react'
import { ChevronDown, PiggyBank, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Pure-browser switching calculator. Compares your incumbent UGC / reviews /
// shoppable-video tool ACV against an equivalent Idukki plan, applies
// volume-based plan-fit logic, and projects 1y / 3y savings.
//
// Mirrors switchingSavings()/INCUMBENT/IDUKKI_PLANS in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools"). Keep the pricing tables
// and math identical across both.

type Incumbent =
  | 'bazaarvoice' | 'yotpo' | 'okendo' | 'loox' | 'judgeme'
  | 'tolstoy' | 'videowise' | 'bambuser' | 'whatmore' | 'quinn'
  | 'flowbox' | 'storista' | 'embedsocial' | 'nosto' | 'other'

const INCUMBENT: Record<Incumbent, { label: string; typical: number; note: string }> = {
  bazaarvoice: { label: 'Bazaarvoice', typical: 120_000, note: 'Enterprise reviews + UGC, 6-figure contracts' },
  yotpo:       { label: 'Yotpo',        typical: 36_000,  note: 'Reviews + loyalty stack' },
  okendo:      { label: 'Okendo',       typical: 18_000,  note: 'Reviews + UGC' },
  loox:        { label: 'Loox',         typical: 4_800,   note: 'Photo reviews only' },
  judgeme:     { label: 'Judge.me',     typical: 1_800,   note: 'Reviews, small basket' },
  tolstoy:     { label: 'Tolstoy',      typical: 28_000,  note: 'Shoppable video, impression-billed' },
  videowise:   { label: 'Videowise',    typical: 24_000,  note: 'Shoppable video' },
  bambuser:    { label: 'Bambuser',     typical: 60_000,  note: 'Live shopping' },
  whatmore:    { label: 'Whatmore',     typical: 18_000,  note: 'Shoppable video' },
  quinn:       { label: 'Quinn',        typical: 14_400,  note: 'Shoppable video' },
  flowbox:     { label: 'Flowbox',      typical: 30_000,  note: 'UGC galleries' },
  storista:    { label: 'Storista',     typical: 18_000,  note: 'UGC galleries' },
  embedsocial: { label: 'EmbedSocial',  typical: 9_600,   note: 'Social walls' },
  nosto:       { label: 'Nosto',        typical: 48_000,  note: 'Personalization + UGC add-on' },
  other:       { label: 'Other / custom', typical: 24_000, note: 'Generic UGC vendor' },
}

const IDUKKI_PLANS = {
  starter:    { price: 3_600,  visitors: 200_000 },
  pro:        { price: 12_000, visitors: 1_000_000 },
  enterprise: { price: 36_000, visitors: Number.POSITIVE_INFINITY },
}

export function SavingsCalculator() {
  const [incumbent, setIncumbent] = React.useState<Incumbent>('bazaarvoice')
  const [currentAcv, setCurrentAcv] = React.useState<number>(INCUMBENT.bazaarvoice.typical)
  const [monthlyVisitors, setMonthlyVisitors] = React.useState<number>(250_000)
  const [implCost, setImplCost] = React.useState<number>(0)

  React.useEffect(() => {
    setCurrentAcv(INCUMBENT[incumbent].typical)
  }, [incumbent])

  const plan = monthlyVisitors > 1_000_000 ? 'enterprise' : monthlyVisitors > 200_000 ? 'pro' : 'starter'
  const idukkiAcv = IDUKKI_PLANS[plan].price
  const annualSavings = Math.max(0, currentAcv - idukkiAcv - implCost)
  const threeYearSavings = annualSavings * 3 + Math.min(implCost, currentAcv)
  const tcoReduction = currentAcv > 0 ? Math.round((annualSavings / currentAcv) * 100) : 0
  const paybackMonths = annualSavings > 0 && implCost > 0 ? Math.max(1, Math.round((implCost / annualSavings) * 12)) : null

  const fmt = (n: number) => '$' + Math.round(n).toLocaleString()

  function reset() {
    setIncumbent('bazaarvoice')
    setCurrentAcv(INCUMBENT.bazaarvoice.typical)
    setMonthlyVisitors(250_000)
    setImplCost(0)
  }

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b p-5 sm:p-6">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">Switching savings</p>
          <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">How much would Idukki save vs your current tool?</h2>
        </div>
        <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground">
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8">
        <div className="space-y-5">
          <Field label="Current tool">
            <div className="relative">
              <select
                value={incumbent}
                onChange={(e) => setIncumbent(e.target.value as Incumbent)}
                className="h-10 w-full appearance-none rounded-md border border-input bg-transparent pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {Object.entries(INCUMBENT).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{INCUMBENT[incumbent].note}</p>
          </Field>

          <Field label="Your current annual contract" hint="Total spend across the platform, integrations and impressions">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input type="number" min={0} step={500} value={currentAcv} onChange={(e) => setCurrentAcv(Number(e.target.value) || 0)} className="pl-7" />
            </div>
          </Field>

          <Field label="Monthly storefront visitors" hint="Determines your Idukki plan tier">
            <Input type="number" min={0} step={1000} value={monthlyVisitors} onChange={(e) => setMonthlyVisitors(Number(e.target.value) || 0)} />
          </Field>

          <Field label="One-off implementation cost (optional)" hint="Migration, training, agency fee, set to zero if Idukki handles it">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input type="number" min={0} step={500} value={implCost} onChange={(e) => setImplCost(Number(e.target.value) || 0)} className="pl-7" />
            </div>
          </Field>
        </div>

        <div className="space-y-3">
          <Card label="Your spend today" value={fmt(currentAcv)} sub={`${INCUMBENT[incumbent].label} ACV`} />
          <Card label="Idukki equivalent" value={fmt(idukkiAcv)} sub={`Suggested plan: Idukki ${plan}, every feature, every source included`} accent />
          <Card label="Annual savings" value={fmt(annualSavings)} sub={`${tcoReduction}% TCO reduction vs ${INCUMBENT[incumbent].label}`} />
          <Card label="3-year savings" value={fmt(threeYearSavings)} sub={paybackMonths ? `Payback in ~${paybackMonths} months including migration` : 'Migration covered by Idukki, no payback to wait for'} large />
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-xs text-muted-foreground sm:text-sm">
          <PiggyBank className="mr-1 inline h-3.5 w-3.5 text-primary" />
          Math runs in the browser. We don&rsquo;t log inputs or store results.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm"><a href="/contact?intent=switch">Get a migration plan</a></Button>
          <Button asChild size="sm" variant="outline"><a href={`/compare/${incumbent}`}>See full comparison</a></Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}

function Card({ label, value, sub, accent, large }: { label: string; value: string; sub?: string; accent?: boolean; large?: boolean }) {
  return (
    <div className={cn(
      'rounded-xl border p-4',
      accent && 'border-primary/40 bg-primary/5',
      large && 'border-foreground/20 bg-foreground text-background',
    )}>
      <div className={cn('text-[11px] uppercase tracking-wider', large ? 'text-background/85' : 'text-muted-foreground')}>{label}</div>
      <div className="mt-1 font-display text-2xl font-medium md:text-3xl">{value}</div>
      {sub && <div className={cn('mt-1 text-[11px]', large ? 'text-background/85' : 'text-muted-foreground')}>{sub}</div>}
    </div>
  )
}
